import { ValueLabel } from "@features/ax/shared/ValueLabel";
import { Badge } from "@shared/ui/badge";
import { Card } from "@shared/ui/card";
import { useMemo } from "react";
import { wxUtils, getStatusColor } from "@features/wx/utils";
import { useLocation } from "react-router-dom";

const badgeColors: Record<string, string> = {
  critical: "bg-red-100 text-red-800 border-red-200",
  high: "bg-orange-100 text-orange-800 border-orange-200",
  medium: "bg-blue-100 text-blue-800 border-blue-200",
  low: "bg-green-100 text-green-800 border-green-200",
};

const getBadgeClass = (priority?: string) =>
  badgeColors[priority?.toLowerCase() || ""] || "bg-gray-100 text-gray-800";

export const InfoCard = ({ jobCard }: { jobCard: any }) => {
  const location = useLocation();
  const isPending = location.pathname.toLowerCase().includes("pending");

  const transformedData = useMemo(
    () => jobCard && wxUtils.transformJobCardDetail(jobCard),
    [jobCard],
  );
  const { jobOverview, serviceOrderData, technicianDetails } =
    transformedData || {};

  const infoItems = [
    {
      label: "Short Name",
      value: serviceOrderData?.shortName || "N/A",
    },
    {
      label: "Utility Service",
      value: serviceOrderData?.utilityServices || "N/A",
    },
    {
      label: "Area",
      value: serviceOrderData?.area || "N/A",
    },
    {
      label: "Sub Area",
      value: serviceOrderData?.subarea || "N/A",
    },
    {
      label: "Expected Completion",
      value: jobOverview?.estimatedCompletion || "N/A",
    },
    {
      label: "Tax Rate",
      value: serviceOrderData?.taxRate || "N/A",
    },
    {
      label: "Created By",
      value: jobCard?.createdUserRemoteName || "N/A",
    },
    {
      label: "Created On",
      value: serviceOrderData?.createdOn || "N/A",
    },
    ...(!isPending
      ? [
          {
            label: "Assigned By",
            value: jobCard?.assignedBy || "N/A",
          },
          {
            label: "Assigned On",
            value: serviceOrderData?.assignedOn || "N/A",
          },
          {
            label: "Assigned To",
            value: technicianDetails?.technicianName || "N/A",
          },
          {
            label: "EMP ID",
            value: technicianDetails?.fid || "N/A",
          },
        ]
      : []),
    ...(serviceOrderData?.statusDisplay?.toLowerCase() === "rejected"
      ? [
          {
            label: "Remark",
            value: serviceOrderData?.remark || "N/A",
            className: "md:col-span-3 lg:col-span-4",
          },
        ]
      : []),
  ];

  return (
    <Card className="p-6">
      <div className="flex justify-between items-start mb-8">
        <div className="flex gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h2 className="text-xl font-bold text-gray-900">
                {serviceOrderData?.soName || "N/A"}
              </h2>

              <div className="flex items-center gap-2">
                <Badge
                  variant="outline"
                  className={`${getBadgeClass(serviceOrderData?.priority)} px-2 py-0.5 text-xs font-medium rounded-full`}
                >
                  {serviceOrderData?.priority || "N/A"}
                </Badge>

                <Badge
                  variant="outline"
                  className="px-2 py-0.5 text-xs font-medium rounded-full"
                >
                  {serviceOrderData?.sopAssociationType || "N/A"}
                </Badge>
                <Badge
                  variant="outline"
                  className={`${getStatusColor(serviceOrderData?.statusDisplay)} px-2 py-0.5 text-xs font-medium rounded-full`}
                >
                  {serviceOrderData?.statusDisplay || "N/A"}
                </Badge>
              </div>
            </div>
          </div>
        </div>

        <div className="flex gap-8 text-center">
          <div>
            <p className="text-xs text-gray-500 font-medium mb-1">SLA Status</p>
            <p className="text-lg font-bold text-blue-600">
              {serviceOrderData?.slaStatus || "N/A"}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500 font-medium mb-1">Due Date</p>
            <p className="text-lg font-bold text-orange-500">
              {jobOverview?.estimatedCompletion || "N/A"}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500 font-medium mb-1">
              Time Remaining
            </p>
            <p className="text-lg font-bold text-green-600">
              {serviceOrderData?.remainingTime || "N/A"}
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-y-8 gap-x-4">
        {infoItems.map((item, index) => (
          <div key={index} className={(item as any).className || ""}>
            <ValueLabel label={item.label} value={item.value} />
          </div>
        ))}
      </div>
    </Card>
  );
};
